import { tileAuthRef, tileCatalogRef } from '@/state/tileAuth';
import { getTenantSetting } from '@/composables/useTenant';
import { canonicalTilePath, normalizeTileAuth, TILE_AUTH_PARAM_NAMES } from '@/utils/tileAuth';

function parseIntegerSetting(value, fallback) {
    const parsedValue = parseInt(value, 10);

    return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function parseHttpsUrl(url) {
    if (typeof url !== 'string' || /[\\\s\u0000-\u001f\u007f]/.test(url)) {
        return null;
    }

    try {
        // Read the original path before URL() can silently remove dot segments.
        const parts = url.match(/^https:\/\/[^/?#]+(\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/i);
        const path = parts && canonicalTilePath(parts[1] || '/');
        const parsedUrl = new URL(url);

        return path !== null && parts && parsedUrl.protocol === 'https:' && !parsedUrl.username && !parsedUrl.password
            ? { host: parsedUrl.host, path }
            : null;
    } catch {
        return null;
    }
}

/**
 * Appende le credenziali CloudFront (Policy, Signature, Key-Pair-Id) come query params
 * soltanto entro uno dei prefissi HTTPS autorizzati di tile_auth v2.
 * URL esterni (es. Mapbox) passano inalterati.
 */
export const signTileUrl = (url, tileAuth) => {
    const auth = normalizeTileAuth(tileAuth);
    const now = Date.now() / 1000;
    if (!auth || auth.expires_at <= now || !url) return url;

    const parsedUrl = parseHttpsUrl(url);
    if (!parsedUrl) return url;

    const grant = auth.grants.find(candidate => candidate.expires_at > now
        && parsedUrl.host === candidate.domain
        && parsedUrl.path.startsWith(canonicalTilePath(candidate.path_prefix)));
    if (!grant) return url;

    // Preserve raw Leaflet placeholders and unrelated query encoding/hash.
    const hashIndex = url.indexOf('#');
    const hash = hashIndex < 0 ? '' : url.slice(hashIndex);
    const withoutHash = hashIndex < 0 ? url : url.slice(0, hashIndex);
    const queryIndex = withoutHash.indexOf('?');
    const base = queryIndex < 0 ? withoutHash : withoutHash.slice(0, queryIndex);
    const query = queryIndex < 0 ? [] : withoutHash.slice(queryIndex + 1).split('&').filter(part => {
        if (!part) return false;
        try {
            const key = decodeURIComponent(part.split('=')[0].replace(/\+/g, ' '));
            return !TILE_AUTH_PARAM_NAMES.includes(key);
        } catch {
            return true;
        }
    });
    query.push(...TILE_AUTH_PARAM_NAMES.map(key => `${key}=${encodeURIComponent(grant.params[key])}`));

    return `${base}?${query.join('&')}${hash}`;
};

export const buildTileRequestUrl = (url, tileAuth) => {
    return signTileUrl(url, tileAuth);
};

function buildTileLayerConfig(layerConfig = {}, overrides = {}) {
    return {
        minZoom:
            layerConfig.minZoom ??
            parseIntegerSetting(
                getTenantSetting("map.min_zoom"),
                16
            ),
        maxZoom: layerConfig.maxZoom ?? 30,
        maxNativeZoom:
            layerConfig.maxNativeZoom ??
            parseIntegerSetting(
                getTenantSetting("map.max_native_zoom"),
                24
            ),
        ...overrides,
    };
}

function createTileLayer(layerUrl, tileAuth, layerConfig = {}, overrides = {}) {
    const registered = catalogLayers().find(layer => layer.url === layerUrl);
    return {
        url: buildTileRequestUrl(layerUrl, tileAuth),
        config: registered ? { ...buildTileLayerConfig(layerConfig, overrides), ...registeredConfig(registered) } : buildTileLayerConfig(layerConfig, overrides),
    };
}

export function getMapCatalog() {
    // Page synchronization owns this state and invalidates Vue consumers on navigation.
    return tileCatalogRef.value;
}

export function getMapUsage(key = 'map.tiles_url') {
    const catalog = getMapCatalog();
    const resolved = catalog?.aliases?.[key] ?? key;
    return Array.isArray(catalog?.usages) ? catalog.usages.find(usage => usage.key === resolved) ?? null : catalog?.usages?.[resolved] ?? null;
}

export function mapViewOptions(view = {}) {
    const result = {};
    for (const [source, target] of Object.entries({ center: 'center', zoom: 'zoom', min_zoom: 'minZoom', max_zoom: 'maxZoom', max_native_zoom: 'maxNativeZoom',
        max_bounds: 'maxBounds', fit_bounds_padding: 'fitBoundsPadding', bearing: 'bearing', rotate: 'rotate' })) {
        if (view?.[source] !== undefined && view[source] !== null) result[target] = view[source];
    }
    return result;
}

export function registeredMapView(key = 'map.tiles_url') {
    return mapViewOptions(getMapUsage(key)?.view);
}

function catalogLayers() {
    return Object.values(getMapCatalog()?.usages ?? {}).flatMap(usage => usage.layers ?? []);
}

function registeredConfig(layer) {
    // Server descriptors already escape attribution for Leaflet's HTML control.
    return { ...layer.config };
}

export function registeredMapLayers(key = 'map.tiles_url') {
    const usage = getMapUsage(key);
    if (!usage) return [];
    const groups = new Map();
    (usage.layers ?? []).forEach((layer, index) => {
        if (typeof layer.url !== 'string' || !layer.url) return;
        const id = layer.group ?? index;
        if (!groups.has(id)) groups.set(id, { group: true, id, name: layer.label || usage.title, layers: [] });
        groups.get(id).layers.push({ url: buildTileRequestUrl(layer.url, tileAuthRef.value), config: registeredConfig(layer) });
    });
    return [...groups.values()];
}

export const generateMapLayers = (url, layerConfig = {}) => {
    let parsedUrl;

    try {
        parsedUrl = JSON.parse(url);
    } catch {
        parsedUrl = [url];
    }

    const urls = (Array.isArray(parsedUrl) ? parsedUrl : [parsedUrl])
        .filter(layerUrl => typeof layerUrl === 'string' && layerUrl.trim().length > 0)
        .filter(layerUrl => !getMapCatalog() || catalogLayers().some(layer => layer.url === layerUrl));
    const tileAuth = tileAuthRef.value;

    if (!urls.length) return [];

    if (layerConfig.stackLayers) {
        const stackedLayers = urls.map((layerUrl, index) => {
            const zIndex = layerConfig.stackOrder === "top-to-bottom"
                ? urls.length - index
                : index + 1;

            return createTileLayer(layerUrl, tileAuth, layerConfig, { zIndex });
        });

        return [
            {
                group: true,
                layers: stackedLayers,
            },
        ];
    }

    return urls.map((layerUrl) => {
        return {
            group: true,
            layers: [createTileLayer(layerUrl, tileAuth, layerConfig)],
        };
    });
};

/** Layer di default — lazy per consentire il refresh delle credenziali tile_auth. */
export const getMapLayers = (keyLayer = "map.tiles_url", layerConfig = {}) =>
    getMapCatalog() ? registeredMapLayers(keyLayer) : generateMapLayers(getTenantSetting(keyLayer), layerConfig);

/** Kroton's seeded base-map order is DTM, CTR, satellite. */
export const getKrotonMapLayers = (layerConfig = {}) =>
    getMapLayers('map.tiles_url', layerConfig).map((group, index) => ({
        ...group,
        id: index,
        name: group.name || ['DTM', 'Carta Tecnica Regionale', 'Satellite'][index] || `Layer ${index + 1}`,
    }));

export const defaultKrotonLayerIndex = (layers) => layers.length > 1 ? 1 : 0;
